import { renderIcon } from '../../icons';
import { createButton, type ButtonHandle, type ButtonVariant } from '../button';
import { lockScroll, unlockScroll } from '../../scroll-lock';
import './modal.css';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalAction {
  label: string;
  variant?: ButtonVariant;
  onClick?: () => void | Promise<void>;
  closeOnClick?: boolean;
}

export interface ModalOptions {
  title: string;
  subtitle?: string;
  size?: ModalSize;
  dismissible?: boolean;
  actions?: ModalAction[];
  content?: (body: HTMLElement) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export interface ModalHandle {
  el: HTMLElement;
  open: () => void;
  close: () => void;
  destroy: () => void;
  setTitle: (title: string) => void;
  setBody: (build: (body: HTMLElement) => void) => void;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]:not([contenteditable="false"])',
].join(',');

const EXIT_DURATION = 150;

function getFocusable(root: HTMLElement): HTMLElement[] {
  const nodes = root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  return Array.from(nodes).filter((n) => {
    if (n.hasAttribute('disabled')) return false;
    if (n.getAttribute('aria-hidden') === 'true') return false;
    const rect = n.getBoundingClientRect();
    return rect.width > 0 || rect.height > 0 || n === document.activeElement;
  });
}

export function createModal(opts: ModalOptions): ModalHandle {
  const size: ModalSize = opts.size ?? 'md';
  const dismissible = opts.dismissible ?? true;

  const overlay = document.createElement('div');
  overlay.className = 'tkos-modal-overlay';
  overlay.setAttribute('data-state', 'open');

  const panel = document.createElement('div');
  panel.className = `tkos-modal tkos-modal--${size}`;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('tabindex', '-1');

  const accent = document.createElement('div');
  accent.className = 'tkos-modal__accent';
  accent.setAttribute('aria-hidden', 'true');
  panel.appendChild(accent);

  const header = document.createElement('div');
  header.className = 'tkos-modal__header';

  const titles = document.createElement('div');
  titles.className = 'tkos-modal__titles';

  const titleEl = document.createElement('h2');
  titleEl.className = 'tkos-modal__title';
  titleEl.id = `tkos-modal-title-${Math.random().toString(36).slice(2, 10)}`;
  titleEl.textContent = opts.title;
  titles.appendChild(titleEl);
  panel.setAttribute('aria-labelledby', titleEl.id);

  let subtitleEl: HTMLParagraphElement | null = null;
  if (opts.subtitle) {
    subtitleEl = document.createElement('p');
    subtitleEl.className = 'tkos-modal__subtitle';
    subtitleEl.textContent = opts.subtitle;
    titles.appendChild(subtitleEl);
  }

  header.appendChild(titles);

  let closeBtn: HTMLButtonElement | null = null;
  if (dismissible) {
    closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'tkos-modal__close';
    closeBtn.setAttribute('aria-label', 'Close dialog');
    closeBtn.appendChild(renderIcon('close', { size: 'sm' }));
    header.appendChild(closeBtn);
  }

  panel.appendChild(header);

  const body = document.createElement('div');
  body.className = 'tkos-modal__body';
  if (opts.content) opts.content(body);
  panel.appendChild(body);

  const actions: ModalAction[] = opts.actions ?? [
    { label: 'Cancel', variant: 'ghost' },
    { label: 'OK', variant: 'primary' },
  ];

  const actionHandles: ButtonHandle[] = [];
  let footer: HTMLDivElement | null = null;

  if (actions.length > 0) {
    footer = document.createElement('div');
    footer.className = 'tkos-modal__footer';

    const leftGroup = document.createElement('div');
    leftGroup.className = 'tkos-modal__footer-group';
    const rightGroup = document.createElement('div');
    rightGroup.className = 'tkos-modal__footer-group';

    actions.forEach((action, index) => {
      const isLeft = index === 0 && actions.length > 1;
      const variant: ButtonVariant = action.variant ?? (isLeft ? 'ghost' : 'primary');
      const closeOnClick = action.closeOnClick ?? true;

      const btn = createButton({
        label: action.label,
        variant,
        size: 'md',
        onClick: () => {
          const run = async (): Promise<void> => {
            try {
              await action.onClick?.();
            } finally {
              if (closeOnClick) close();
            }
          };
          void run();
        },
      });
      actionHandles.push(btn);
      (isLeft ? leftGroup : rightGroup).appendChild(btn.el);
    });

    footer.appendChild(leftGroup);
    footer.appendChild(rightGroup);
    panel.appendChild(footer);
  }

  overlay.appendChild(panel);

  let isOpen = false;
  let previousFocus: HTMLElement | null = null;
  let closeTimer: number | null = null;

  const onKeyDown = (e: KeyboardEvent): void => {
    if (!isOpen) return;
    if (e.key === 'Escape' && dismissible) {
      e.preventDefault();
      close();
      return;
    }
    if (e.key === 'Tab') {
      const focusable = getFocusable(panel);
      if (focusable.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  };

  const onOverlayMouseDown = (e: MouseEvent): void => {
    if (!dismissible) return;
    if (e.target === overlay) close();
  };

  const onCloseClick = (): void => {
    close();
  };

  const open = (): void => {
    if (isOpen) return;
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    isOpen = true;
    previousFocus = (document.activeElement as HTMLElement | null) ?? null;
    lockScroll();

    overlay.setAttribute('data-state', 'open');
    if (!overlay.isConnected) document.body.appendChild(overlay);

    document.addEventListener('keydown', onKeyDown, true);
    overlay.addEventListener('mousedown', onOverlayMouseDown);
    if (closeBtn) closeBtn.addEventListener('click', onCloseClick);

    requestAnimationFrame(() => {
      const focusable = getFocusable(panel);
      const target = focusable.find((n) => n !== closeBtn) ?? focusable[0] ?? panel;
      target.focus();
    });

    opts.onOpen?.();
  };

  const close = (): void => {
    if (!isOpen) return;
    isOpen = false;

    overlay.setAttribute('data-state', 'closing');
    document.removeEventListener('keydown', onKeyDown, true);
    overlay.removeEventListener('mousedown', onOverlayMouseDown);
    if (closeBtn) closeBtn.removeEventListener('click', onCloseClick);

    unlockScroll();

    closeTimer = window.setTimeout(() => {
      closeTimer = null;
      if (overlay.isConnected) overlay.remove();
      if (previousFocus && document.contains(previousFocus)) {
        previousFocus.focus();
      }
      previousFocus = null;
      opts.onClose?.();
    }, EXIT_DURATION);
  };

  const destroy = (): void => {
    if (isOpen) {
      isOpen = false;
      document.removeEventListener('keydown', onKeyDown, true);
      overlay.removeEventListener('mousedown', onOverlayMouseDown);
      if (closeBtn) closeBtn.removeEventListener('click', onCloseClick);
      unlockScroll();
    }
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }
    actionHandles.forEach((h) => h.destroy());
    if (overlay.isConnected) overlay.remove();
  };

  const setTitle = (next: string): void => {
    titleEl.textContent = next;
  };

  const setBody = (build: (body: HTMLElement) => void): void => {
    body.textContent = '';
    build(body);
  };

  return { el: overlay, open, close, destroy, setTitle, setBody };
}
